import first from "lodash/first";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useCurrentPageType } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl, getYoutubeThumbnailUrl, notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { scmpShortsCard$key } from "scmp-app/queries/__generated__/scmpShortsCard.graphql";

import {
  Container,
  Headline,
  ImageWrapper,
  PlayIconWrapper,
  StyledImage,
  StyledPlayIcon,
} from "./styles";

type Props = {
  className?: string;
  reference: scmpShortsCard$key;
};

export const ScmpShortsCard: FunctionComponent<Props> = ({ className, reference }) => {
  const video = useFragment(
    graphql`
      fragment scmpShortsCard on Video {
        entityId
        headline
        urlAlias
        youtubeId
        images {
          portraitStyle: style(filter: { style: "portrait" }) {
            url
          }
        }
        ...entityLink
      }
    `,
    reference,
  );

  const isValid = notEmpty(video.entityId) && notEmpty(video.youtubeId);

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const mainImageUrl = useMemo(() => {
    if (notEmpty(video.youtubeId)) {
      const youtubeThumb = first(getYoutubeThumbnailUrl(video.youtubeId));
      if (youtubeThumb) return youtubeThumb;
    }
    const image = first(video?.images);
    return image?.portraitStyle?.url ?? "";
  }, [video.youtubeId, video?.images]);

  if (!isValid) return null;

  return (
    <EntityLink
      className={className}
      onClick={() => {
        sendGA4Tracking({
          action: "click",
          category: "recirculation",
          customized_parameters: {
            article_id: video.entityId ?? "",
            destination_url: getAbsoluteUrl(video.urlAlias),
            page_type: currentPageType,
            widget_name: "scmp_shorts",
          },
          subcategory: "widget",
        });
      }}
      reference={video}
    >
      <Container>
        <ImageWrapper>
          <StyledImage
            $aspectRatio="9/16"
            alt={video?.headline ?? ""}
            lazyload
            src={mainImageUrl}
          />
          <PlayIconWrapper>
            <StyledPlayIcon />
          </PlayIconWrapper>
        </ImageWrapper>
        <Headline>{video?.headline}</Headline>
      </Container>
    </EntityLink>
  );
};

ScmpShortsCard.displayName = "ScmpShortsCard";
